import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Edit, LogOut } from 'lucide-react';
import ProfileHeader from '@/components/profile/ProfileHeader';
import ProfileSidebar from '@/components/profile/ProfileSidebar';
import ProfileForm from '@/components/profile/ProfileForm';
import ReleasesList from '@/components/profile/ReleasesList';
import AwardMemberships from '@/components/profile/AwardMemberships';
import { UserProfile, Release, DailyStreak } from '@/types';
import { useToast } from '@/hooks/use-toast';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useUserStats } from '@/hooks/useUserStats';
import { supabase } from '@/integrations/supabase/client';

const getDummyStreakData = (): DailyStreak => {
  const today = new Date();
  return {
    currentStreak: 3,
    longestStreak: 7,
    lastLoginDate: today,
    streakHistory: Array(7).fill(null).map((_, i) => ({
      date: new Date(today.getTime() - (6 - i) * 24 * 60 * 60 * 1000),
      completed: i < 6
    })),
    rewards: [
      { streakDays: 3, reward: "50 XP", claimed: true },
      { streakDays: 7, reward: "100 XP + Badge", claimed: false },
      { streakDays: 14, reward: "250 XP + Badge", claimed: false },
      { streakDays: 30, reward: "500 XP + Exclusive Badge", claimed: false }
    ]
  };
};

export const getDummyUserProfile = (): UserProfile => {
  return {
    userId: "at-123456",
    name: "Alex Turner",
    displayName: "alexturner",
    bio: "Electronic music producer and DJ specializing in house and techno. Always looking for vocalists and other producers to collaborate with on new projects.",
    role: "Producer/DJ",
    location: "London, UK",
    avatarUrl: "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d",
    genres: ["House", "Techno", "Electronic", "Deep House"],
    socialLinks: {
      spotify: "https://spotify.com/artist/alexturner",
      instagram: "https://instagram.com/alexturner",
      youtube: "https://youtube.com/alexturner",
      website: "https://alexturnermusic.com"
    },
    memberSince: new Date(2023, 2, 15),
    totalReleases: 12,
    totalStreams: 32478,
    followers: 842,
    following: 56,
    xp: 1250,
    level: 4,
    dailyStreak: getDummyStreakData()
  };
};

export const getDummyReleases = (): Release[] => {
  return [
    {
      id: "rel-001",
      userId: "at-123456",
      title: "Late Nights EP",
      type: "ep",
      coverArt: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6",
      releaseDate: new Date(2023, 4, 15),
      streams: 2450,
      status: "released",
      platforms: ["Spotify", "Apple Music", "SoundCloud"]
    },
    {
      id: "rel-002",
      userId: "at-123456",
      title: "Digital Dreams",
      type: "single",
      coverArt: "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b",
      releaseDate: new Date(2023, 0, 23),
      streams: 3782,
      status: "released",
      platforms: ["Spotify", "Apple Music", "SoundCloud", "YouTube Music"]
    }
  ];
};

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Get real user profile from database
  const { profile: userProfile, isLoading } = useUserProfile();
  const { data: userStats, isLoading: statsLoading } = useUserStats();
  const releases = getDummyReleases();
  
  // Merge stats into userProfile
  const enrichedProfile = userProfile ? {
    ...userProfile,
    totalReleases: userStats?.releasedTracks || 0,
    totalStreams: userStats?.totalStreams || 0,
    followers: userStats?.followers || 0,
    following: userStats?.following || 0,
  } : undefined;
  
  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Logged out successfully",
      description: "You have been logged out of your account"
    });
    navigate('/auth/signin');
  };

  if (isLoading || statsLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-28 px-4 max-w-5xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-muted rounded-lg"></div>
            <div className="h-64 bg-muted rounded-lg"></div>
          </div>
        </main>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-28 px-4 max-w-5xl mx-auto">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Profile not found. Please log in.</p>
          </div>
        </main>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-28 px-4 max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8">
          <ProfileHeader 
            title="Your Profile" 
            description="Manage your personal information and preferences"
          />
          
          <div className="flex gap-2 mt-4 sm:mt-0">
            <Button variant="outline" className="gap-2">
              <Edit className="h-4 w-4" />
              <span>Edit</span>
            </Button>
            <Button variant="outline" className="gap-2 text-destructive" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6 animate-slide-up">
          <ProfileSidebar 
            name={userProfile.name}
            role={userProfile.role}
            genres={userProfile.genres}
            avatarSrc={userProfile.avatarUrl}
            userProfile={enrichedProfile}
          />
          
          <div>
            <Tabs defaultValue="profile" className="animate-slide-up">
              <TabsList className="w-full grid grid-cols-3 mb-6">
                <TabsTrigger value="profile">Profile Info</TabsTrigger>
                <TabsTrigger value="releases">My Releases</TabsTrigger>
                <TabsTrigger value="awards">Award Memberships</TabsTrigger>
              </TabsList>
              
              <TabsContent value="profile">
                <ProfileForm profile={userProfile} />
              </TabsContent>
              
              <TabsContent value="releases">
                <ReleasesList releases={releases} />
              </TabsContent>
              
              <TabsContent value="awards">
                <AwardMemberships />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
